import React from "react";
import { createPortal } from "react-dom";

import {
  ChevLeftIcon,
  CloseIcon,
  MailIcon,
  PhoneIcon,
  WhatsappIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";
import MailTo from "@/components/ui/MailTo";
import { CONTACT } from "@/constants/contact";

import { twMerge } from "tailwind-merge";

interface UnlockPremiumModalProps {
  title: string;
  description?: string;
  contactUsDesc: string;
  emailSubject?: string;
  emailBody?: string;
  onClose?: () => void;
  onBack?: () => void;
  datasetSection?: React.ReactNode;
  classNameContainer?: string;
  classNameContent?: string;
}

export const UnlockPremiumModal = ({
  title,
  description,
  contactUsDesc,
  emailSubject = "",
  emailBody = "",
  onClose,
  onBack,
  datasetSection,
  classNameContainer,
  classNameContent
}: UnlockPremiumModalProps) => {
  const modalContent = (
    <>
      {onClose && (
        <button
          type="button"
          aria-label="Close modal"
          className="fixed inset-0 bg-black/50 cursor-auto z-[9998]"
          onClick={onClose}
        />
      )}
      <div
        data-testid="unlock-premium-dialog"
        className={twMerge(
          `fixed top-1/2 left-1/2 w-[456px] bg-white rounded-xl shadow-lg transform -translate-x-1/2 -translate-y-1/2 p-4 flex flex-col gap-4 z-[9999]`,
          classNameContainer
        )}
      >
        <div className="space-y-1 text-left">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {onBack && (
                <div className="h-6 flex items-center justify-center">
                  <ChevLeftIcon
                    data-testid="unlock-back-button"
                    className="w-4 h-4 cursor-pointer fill-dim-gray"
                    onClick={onBack}
                  />
                </div>
              )}
              <div className="text-txtHeading text-[20px] font-semibold text-left">
                {title}
              </div>
            </div>
            {onClose && (
              <CloseIcon
                title="close"
                className="w-6 h-6 cursor-pointer"
                onClick={onClose}
              />
            )}
          </div>
          <div className="text-txtBody text-[12px]">{description}</div>
          {datasetSection}
        </div>
        <div className={twMerge("flex flex-col gap-4")}>
          <div className={twMerge("text-left", classNameContent)}>
            {onClose && (
              <p className="text-sm text-txtHeading font-semibold mb-2">
                Contact Us
              </p>
            )}
            <p className="text-txtBody text-[12px] text-left">
              {contactUsDesc}
            </p>
          </div>
          <div className="space-y-4">
            <div className="rounded-lg border border-gainsboro p-2 grid grid-cols-2 text-left">
              <div className="flex items-center gap-2 mb-2">
                <PhoneIcon className="w-5 h-5 fill-txtBody" />
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-txtBody">Phone Number</span>
                  <span className="text-[12px] font-semibold text-txtBody">
                    {CONTACT.PHONE_NUMBER}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <WhatsappIcon className="w-5 h-5 fill-txtBody" />
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-txtBody">WhatsApp</span>
                  <span className="text-[12px] font-semibold text-txtBody">
                    {CONTACT.PHONE_NUMBER}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <MailIcon className="w-5 h-5 fill-txtBody" />
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-txtBody">Email</span>
                  <MailTo
                    email={CONTACT.EMAIL}
                    subject={emailSubject}
                    body={emailBody}
                  />
                </div>
              </div>
            </div>
            <div>
              {onClose && (
                <Button
                  className="w-full h-[40px] rounded-xl font-semibold text-sm"
                  onClick={onClose}
                >
                  Got It
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return onClose ? createPortal(modalContent, document.body) : modalContent;
};
